import { useMutation } from "@tanstack/react-query";

import { queryClient } from "@config/queryClient";
import useMeetingStore from "@store/useMeetingStore";
import usePlatformStore from "@store/usePlatformStore";
import meetingService from "@services/meeting.service";
import { useBugsnagErrorHandler } from "@hooks/useBugsnagErrorHandler";
import { DisconnectedError } from "@app-types/errors";
import { ERROR_STRINGS } from "@config/messages";
import { IMeetingResponse } from "@interfaces/meeting.interface";
import { UpdateMeetingRequest } from "@app-types/requests.type";
import {
  MEETING_QUERY_KEY,
  SUGGESTED_TAGGING_OPTIONS_QUERY_KEY,
  OBJECT_TAGS_QUERY_KEY,
  UPDATE_MEETING_MUTATION_KEY,
} from "@config/query";

export function useUpdateMeeting(onError?: () => void) {
  const { notifyBugsnag } = useBugsnagErrorHandler();
  const { itemId } = usePlatformStore();
  const { meetingId } = useMeetingStore();

  return useMutation({
    mutationKey: [UPDATE_MEETING_MUTATION_KEY, meetingId],
    mutationFn: async (data: UpdateMeetingRequest) => {
      return await meetingService.updateMeeting(meetingId, data.payload);
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: [MEETING_QUERY_KEY, itemId] });

      const prev = queryClient.getQueryData<IMeetingResponse>([MEETING_QUERY_KEY, itemId]);

      queryClient.setQueryData([MEETING_QUERY_KEY, itemId], (old: IMeetingResponse) => {
        if (!old) return old;

        const attendees = variables?.attendees || old.meeting?.attendees || [];
        const people = variables?.people || old.meeting?.people || [];

        return {
          ...old,
          meeting: { ...old.meeting, attendees, people },
        };
      });

      return { prev };
    },
    onSuccess: (response) => {
      queryClient.setQueryData([MEETING_QUERY_KEY, itemId], (old: IMeetingResponse) => {
        if (!old) return response;

        // merge attendees
        const serverAttendees = response.meeting?.attendees || [];
        const localAttendees = old.meeting?.attendees || [];
        const serverPeople = response.meeting?.people || [];
        const localPeople = old.meeting?.people || [];

        const attendees = Array.from(
          new Map([...serverAttendees, ...localAttendees].map((a) => [a.id, a]))
        ).map(([_, v]) => v);
        const people = Array.from(
          new Map([...serverPeople, ...localPeople].map((a) => [a.id, a]))
        ).map(([_, v]) => v);

        return {
          ...old,
          meeting: { ...old.meeting, ...response.meeting, attendees, people },
        };
      });
      void queryClient.invalidateQueries({
        queryKey: [OBJECT_TAGS_QUERY_KEY, "meetings.meeting", meetingId],
      });
      void queryClient.invalidateQueries({
        queryKey: [SUGGESTED_TAGGING_OPTIONS_QUERY_KEY, "meetings.meeting", meetingId],
      });
    },
    onError: (error, _variables, context) => {
      if (error instanceof DisconnectedError) {
        queryClient.setQueryData([MEETING_QUERY_KEY, itemId], context?.prev);
        return;
      }
      notifyBugsnag(error, ERROR_STRINGS.UPDATE_MEETING);
      void queryClient.invalidateQueries({ queryKey: [MEETING_QUERY_KEY, itemId] });

      onError?.();
    },
  });
}
